import AnalyticsClientsBlock from './AnalyticsClientsBlock';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3, ShoppingCart, DollarSign, Users, TrendingUp, Download,
  ArrowUp, ArrowDown, Filter, RotateCcw, Activity, Wand2,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, ComposedChart,
} from 'recharts';
import EVCAnalytics from './EVCAnalytics';
import { GeographyBlock } from './analytics/GeographyBlock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n, ccy = 'EUR') => {
  try { return new Intl.NumberFormat('en-EU', { style: 'currency', currency: ccy, maximumFractionDigits: 2 }).format(Number(n) || 0); }
  catch (_) { return `€${(Number(n) || 0).toFixed(2)}`; }
};
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');
// Tile-friendly money: no cents, so €6,875.00 can't overflow into the icon.
const fmtMoney0 = (n) => `€${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const COLORS = ['#06b6d4', '#2563eb', '#7c3aed', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#84cc16'];

const ClickableKpi = ({ onClick, ...rest }) => (
  <div onClick={onClick} className="cursor-pointer hover:-translate-y-0.5 transition-transform">
    <Kpi {...rest} />
  </div>
);

const Kpi = ({ icon: Icon, label, value, sub, growth, accent = 'cyan', testid, tooltip }) => {
  const accents = {
    cyan:    'from-cyan-500 to-blue-600',
    violet:  'from-violet-500 to-fuchsia-600',
    emerald: 'from-emerald-500 to-teal-600',
    amber:   'from-amber-500 to-orange-600',
    rose:    'from-rose-500 to-red-600',
  };
  return (
    <div data-testid={testid} title={tooltip || ''} className={`bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-sm ${tooltip ? 'cursor-help' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 leading-tight">{label}</p>
          <p className="text-xl sm:text-2xl font-extrabold text-slate-800 mt-1 leading-none tabular-nums">{value}</p>
          <div className="flex items-center gap-2 mt-1.5">
            {sub && <p className="text-[11px] text-slate-500 truncate">{sub}</p>}
            {growth != null && (
              <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${growth >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {growth >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                {Math.abs(growth).toFixed(1)}%
              </span>
            )}
          </div>
        </div>
        <div className={`w-9 h-9 flex-shrink-0 rounded-lg bg-gradient-to-br ${accents[accent]} text-white flex items-center justify-center shadow`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
};

/**
 * EcuFinderKpi — KPI tile for "Smart ECU Finder adoption". Renders the
 * adoption % as a big number, the X-of-Y sub-line, AND a tiny inline
 * sparkline of per-bucket adoption % from `data.buckets`. Click → admin
 * Orders list filtered to ecu_finder_only=true (via the `onClick` prop).
 *
 * Matches the wand-badge cyan accent so the visual language across the
 * order detail badge + dashboard card + analytics tile is consistent.
 */
const EcuFinderKpi = ({ data, loading, onClick }) => {
  const total      = data?.total ?? 0;
  const viaFinder  = data?.via_finder ?? 0;
  const pct        = data?.adoption_pct;
  const buckets    = Array.isArray(data?.buckets) ? data.buckets : [];

  // Build the sparkline path from buckets. Null pcts (days with zero
  // orders) are skipped so the line doesn't dive to 0 — we just don't
  // draw a point. SVG box is 100×24, scale x by index, y inverted.
  const W = 100, H = 24, PAD = 2;
  const pts = buckets
    .map((b, i) => ({ x: i, y: b.adoption_pct }))
    .filter((p) => p.y !== null && p.y !== undefined && Number.isFinite(p.y));
  let pathD = '';
  if (pts.length >= 2) {
    const step = (W - PAD * 2) / Math.max(buckets.length - 1, 1);
    pathD = pts.map((p, i) =>
      `${i === 0 ? 'M' : 'L'} ${(PAD + p.x * step).toFixed(2)} ${(H - PAD - (p.y / 100) * (H - PAD * 2)).toFixed(2)}`
    ).join(' ');
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="an-kpi-ecu-finder"
      title="Customers who used the inline Find-by-ECU smart selector on Step 1. Click to filter the Orders list."
      className="text-left bg-white rounded-xl p-3 sm:p-4 border border-cyan-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-400"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-cyan-700 truncate">ECU Finder</p>
          <p className="text-xl sm:text-2xl font-extrabold text-slate-800 mt-1 leading-none tabular-nums">
            {loading ? '…' : (pct === null || pct === undefined ? '—' : `${pct}%`)}
          </p>
          <p className="text-[11px] text-slate-500 truncate mt-1.5">
            {loading ? '' : `${viaFinder} of ${total} via finder`}
          </p>
        </div>
        <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-gradient-to-br from-cyan-500 to-sky-600 text-white flex items-center justify-center shadow">
          <Wand2 className="w-4 h-4" />
        </div>
      </div>
      {/* Sparkline — only render when we have at least 2 plottable points. */}
      {pathD && (
        <svg
          data-testid="an-kpi-ecu-finder-sparkline"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="w-full h-6 mt-2"
          aria-hidden="true"
        >
          <path d={pathD} fill="none" stroke="#0891b2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          {/* Last-point dot for emphasis */}
          {(() => {
            const last = pts[pts.length - 1];
            const step = (W - PAD * 2) / Math.max(buckets.length - 1, 1);
            return (
              <circle
                cx={(PAD + last.x * step).toFixed(2)}
                cy={(H - PAD - (last.y / 100) * (H - PAD * 2)).toFixed(2)}
                r="1.8"
                fill="#0891b2"
              />
            );
          })()}
        </svg>
      )}
    </button>
  );
};

/**
 * Custom tooltip for the recharges ComposedChart. Renders one row per stack
 * (PayPal/Stripe/Bank/Manual/Bonus) plus two summary rows at the bottom:
 *   • Total without bonus = real-money sum (the stack minus the amber bar)
 *   • Total                = full stack height including bonus (matches the
 *                            black trend line)
 * Shown the way an admin reads cashflow: "what came in cash" vs "with gifts".
 */
/**
 * Tooltip for the Orders metric chart. Shows count + money side-by-side,
 * answering the core business question for the hovered period:
 *   • how many orders were created
 *   • how many of those completed
 *   • total order amount + completed amount + AOV
 */
function OrdersTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const b = payload[0].payload || {};
  const aov = b.completed_count ? (b.completed_amount || 0) / b.completed_count
            : (b.count ? (b.total_amount || 0) / b.count : 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-[11px] min-w-[220px]" data-testid="an-orders-tooltip">
      <div className="font-bold text-slate-800 mb-1.5">{label}</div>
      <div className="space-y-0.5">
        <Row k="Orders"            v={fmtNum(b.count || 0)}                       color="#06b6d4" />
        <Row k="Completed"         v={fmtNum(b.completed_count || 0)}             color="#10b981" />
        {Number(b.cancelled_count) > 0 && (
          <Row k="Cancelled"       v={fmtNum(b.cancelled_count)}                  color="#ef4444" />
        )}
      </div>
      <div className="mt-1.5 pt-1.5 border-t border-slate-100 space-y-0.5">
        <Row k="Total amount"      v={fmtMoney(b.total_amount || 0)}     bold     color="#0f172a" />
        <Row k="Completed amount"  v={fmtMoney(b.completed_amount || 0)} bold     color="#10b981" />
        {Number(b.cancelled_amount) > 0 && (
          <Row k="Cancelled amount" v={fmtMoney(b.cancelled_amount)}              color="#ef4444" />
        )}
        <Row k="Avg order value"   v={fmtMoney(aov)}                              color="#64748b" />
      </div>
    </div>
  );
}

/** Tooltip for the Revenue metric — count + money per bucket. */
function RevenueTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const b = payload[0].payload || {};
  const aov = b.count ? (b.amount || 0) / b.count : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-[11px] min-w-[200px]" data-testid="an-revenue-tooltip">
      <div className="font-bold text-slate-800 mb-1.5">{label}</div>
      <Row k="Completed orders" v={fmtNum(b.count || 0)}        color="#10b981" />
      <Row k="Revenue"          v={fmtMoney(b.amount || 0)} bold color="#0f172a" />
      <Row k="Avg order value"  v={fmtMoney(aov)}               color="#64748b" />
    </div>
  );
}

/** One key/value line for the custom tooltips above. */
function Row({ k, v, color = '#64748b', bold = false }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span className="text-slate-600">{k}</span>
      </span>
      <span className={`font-mono tabular-nums ${bold ? 'font-bold text-slate-900' : 'text-slate-800'}`}>{v}</span>
    </div>
  );
}

// Single tile in the Problem queue grid on the Analytics page. Red +
// bold count when there's a backlog, muted "All clear ✓" when zero —
// the green-checkmark variant is intentional so a quick scan tells the
// admin "nothing to do here" without having to read the number.
function ProblemTile({ label, hint, value, testid, onClick }) {
  const trouble = value > 0;
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      {...(onClick ? { type: 'button', onClick, title: 'Open the list behind this counter' } : {})}
      data-testid={testid}
      className={`text-left w-full p-3 rounded-lg border transition-colors ${trouble ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'} ${onClick ? 'hover:brightness-95 cursor-pointer' : ''}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600 truncate">{label}</p>
        <span className={`font-mono font-bold tabular-nums text-lg ${trouble ? 'text-red-700' : 'text-emerald-700'}`}>
          {trouble ? value : '✓'}
        </span>
      </div>
      <p className="text-[10px] text-slate-500 mt-0.5">{hint}</p>
    </Tag>
  );
}

function RechargeTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  // payload[0].payload has the raw bucket dict (paypal, stripe, bank, manual, bonus, real, value).
  const b = payload[0].payload || {};
  const rows = [
    { label: 'PayPal', value: b.paypal, color: '#3b82f6' },
    { label: 'Stripe', value: b.stripe, color: '#8b5cf6' },
    { label: 'Bank',   value: b.bank,   color: '#10b981' },
    { label: 'Manual', value: b.manual, color: '#64748b' },
    { label: 'Bonus',  value: b.bonus,  color: '#f59e0b' },
  ].filter((r) => Number(r.value) > 0); // hide zero rows for compactness

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-[11px]" data-testid="an-recharge-tooltip">
      <div className="font-bold text-slate-800 mb-1.5">{label}</div>
      {rows.length === 0 && <div className="text-slate-400">No activity</div>}
      {rows.map((r) => (
        <div key={r.label} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
            <span className="text-slate-600">{r.label}</span>
          </span>
          <span className="font-mono tabular-nums text-slate-800">{fmtMoney(r.value)}</span>
        </div>
      ))}
      <div className="mt-1.5 pt-1.5 border-t border-slate-100 space-y-0.5">
        <div className="flex items-center justify-between gap-4">
          <span className="text-cyan-700 font-semibold">Total without bonus</span>
          <span className="font-mono tabular-nums text-cyan-700 font-bold">{fmtMoney(b.real || 0)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-slate-900 font-semibold">Total</span>
          <span className="font-mono tabular-nums text-slate-900 font-bold">{fmtMoney(b.value || 0)}</span>
        </div>
      </div>
    </div>
  );
}


export default function AnalyticsPanel({ token, go }) {
  const [range, setRange] = useState('this_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');
  const [metric, setMetric] = useState('orders');
  const [filters, setFilters] = useState({ manufacturer: '', ecu_type: '', status: '', user_email: '', account_id: '' });
  const [clientsTab, setClientsTab] = useState('new');
  const [clientsOnlyWithOrders, setClientsOnlyWithOrders] = useState(true);
  const [topMetric, setTopMetric] = useState('revenue');
  // Toggle between count-based and revenue-based ranking for the Top
  // manufacturers / Top ECU charts. Keeps the same backend payload —
  // we just re-sort + re-key client-side.
  const [breakdownMode, setBreakdownMode] = useState('orders'); // 'orders' | 'revenue'

  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState({ buckets: [], total: 0 });
  const [breakdown, setBreakdown] = useState(null);
  const [topCust, setTopCust] = useState({ items: [] });
  const [topVehicles, setTopVehicles] = useState(null);
  const [problemQueue, setProblemQueue] = useState(null);
  // ECU-finder adoption — { total, via_finder, adoption_pct, buckets[] }
  // The KPI tile + sparkline read from this same payload.
  const [ecuFinderAdoption, setEcuFinderAdoption] = useState(null);
  const [loading, setLoading] = useState(true);

  // Build a query-string from current range/filters — applies to all endpoints.
  const qs = useMemo(() => {
    const p = new URLSearchParams({ range });
    if (range === 'custom') {
      if (customStart) p.append('start', customStart);
      if (customEnd)   p.append('end',   customEnd);
    }
    return p;
  }, [range, customStart, customEnd]);

  const filtersQs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString();
  }, [filters]);
  // How many filters are currently applied — drives a badge in the UI.
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((v) => !!v).length,
    [filters],
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [s, t, b, c, tv, pq, ef] = await Promise.all([
        fetch(`${API_URL}/api/admin/analytics/summary?${qs.toString()}`, { headers }).then((r) => r.json()),
        fetch(`${API_URL}/api/admin/analytics/timeseries?${qs.toString()}&metric=${metric}`, { headers }).then((r) => r.json()),
        fetch(`${API_URL}/api/admin/analytics/orders/breakdown?${qs.toString()}${filtersQs ? '&' + filtersQs : ''}`, { headers }).then((r) => r.json()),
        fetch(`${API_URL}/api/admin/analytics/orders/top-customers?${qs.toString()}&metric=${topMetric}`, { headers }).then((r) => r.json()),
        // New ops blocks — degrade silently if missing (older backends
        // didn't ship these endpoints, the rest of the page is fine).
        fetch(`${API_URL}/api/admin/analytics/top-vehicles?${qs.toString()}&limit=10`, { headers })
          .then((r) => r.ok ? r.json() : null).catch(() => null),
        fetch(`${API_URL}/api/admin/analytics/problem-queue`, { headers })
          .then((r) => r.ok ? r.json() : null).catch(() => null),
        // Smart ECU-finder adoption + per-bucket sparkline data.
        // Same range as everything else on the page.
        fetch(`${API_URL}/api/admin/analytics/ecu-finder-adoption?${qs.toString()}&series=true`, { headers })
          .then((r) => r.ok ? r.json() : null).catch(() => null),
      ]);
      setSummary(s); setSeries(t); setBreakdown(b); setTopCust(c);
      setTopVehicles(tv); setProblemQueue(pq); setEcuFinderAdoption(ef);
    } catch (_) { /* surfaced as empty UI */ }
    finally { setLoading(false); }
  }, [token, qs, metric, filtersQs, topMetric]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const downloadCsv = async (kind) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/analytics/export?${qs.toString()}&kind=${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { alert('Export failed'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_${kind}_${range}_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Export error: ' + e.message); }
  };

  const k = summary || {};
  const kpiOrders     = k.orders || {};
  const kpiRecharges  = k.recharges || {};
  const kpiUsers      = k.users || {};
  const growth        = k.growth || {};
  const cmpLabel      = k.compare?.period_label ? ` Compared with ${k.compare.period_label} (orders ${k.compare.orders?.value ?? 0}, revenue €${k.compare.revenue?.value ?? 0}).` : '';
  const openClients = (tab, withOrders) => {
    setClientsTab(tab);
    setClientsOnlyWithOrders(withOrders);
    document.querySelector('[data-testid="an-clients-block"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ───────── deep-links ─────────
  // Every click-through carries the currently selected period so the
  // target list shows exactly the rows behind the number that was clicked.
  const periodFrom = summary?.period?.from || '';
  const periodTo   = summary?.period?.to   || '';
  const goOrders = (extra = {}) => {
    if (typeof go !== 'function') return;
    go('orders', { orders: { date_start: periodFrom, date_end: periodTo, ...extra } });
  };
  const goTx = (extra = {}) => {
    if (typeof go !== 'function') return;
    go('transactions', { transactions: { date_start: periodFrom, date_end: periodTo, ...extra } });
  };
  // Chart bucket label → a concrete YYYY-MM-DD range.
  // day → "2026-06-25", month → "2026-06", hour → "2026-06-25 14:00".
  const bucketRange = (label) => {
    const l = String(label || '');
    if (/^\d{4}-\d{2}-\d{2}/.test(l)) {
      const d = l.slice(0, 10);
      return { date_start: d, date_end: d };
    }
    if (/^\d{4}-\d{2}$/.test(l)) {
      const [y, m] = l.split('-').map(Number);
      const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
      return { date_start: `${l}-01`, date_end: `${l}-${String(last).padStart(2, '0')}` };
    }
    // "HH:00" buckets (today/yesterday) — fall back to the whole period.
    return { date_start: periodFrom, date_end: periodTo };
  };
  // Bar / dot click inside the time-series chart. Recharts hands us the
  // rectangle props, with the raw bucket under `payload`.
  const onBucketClick = (d) => onSeriesClick({ activeLabel: d?.payload?.label || d?.label });
  // Click anywhere on the time-series chart → the rows behind that bucket.
  const onSeriesClick = (e) => {
    const label = e?.activeLabel;
    if (!label) return;
    const r = bucketRange(label);
    if (metric === 'orders')        goOrders(r);
    else if (metric === 'revenue')  goOrders({ ...r, status: 'completed' });
    else if (metric === 'recharges') goTx({ ...r, category: 'top_ups' });
    else openClients('new', false);
  };

  return (
    <div className="p-4 md:p-6 w-full max-w-[1500px] mx-auto" data-testid="analytics-panel">
      {/* Header + range selector */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2.5">
            <BarChart3 className="w-7 h-7 text-cyan-600" /> Analytics
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Orders, revenue, recharges, registrations — all in one place.
            {summary?.period?.label && (
              <span className="ml-2 font-semibold text-slate-600" data-testid="an-period-label">{summary.period.label}</span>
            )}
            {summary?.compare?.period_label && (
              <span className="ml-1.5 text-slate-400">· vs {summary.compare.period_label}</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[
            { id: 'today',       label: 'Today' },
            { id: 'yesterday',   label: 'Yesterday' },
            { id: 'week',        label: 'Week' },
            { id: 'this_month',  label: 'This month' },
            { id: 'prev_month',  label: 'Prev month' },
            { id: 'this_year',   label: 'This year' },
            { id: 'all',         label: 'All time' },
            { id: 'custom',      label: 'Custom' },
          ].map((p) => (
            <button
              key={p.id}
              data-testid={`an-range-${p.id}`}
              onClick={() => setRange(p.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                range === p.id ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >{p.label}</button>
          ))}

          {/* Year selector — auto-rebuilds every render so when 2027 rolls
              around it appears at the top of the list with no code change. */}
          <div className="flex items-center gap-1 ml-1 pl-2 border-l border-slate-200">
            <select
              data-testid="an-range-year-select"
              value={typeof range === 'string' && range.startsWith('year_') ? range : ''}
              onChange={(e) => e.target.value && setRange(e.target.value)}
              className={`px-2 py-1.5 rounded-full text-xs font-bold transition-all border ${
                typeof range === 'string' && range.startsWith('year_')
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white border-transparent shadow'
                  : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
              }`}
            >
              <option value="">Year…</option>
              {Array.from(
                { length: new Date().getFullYear() - 2017 + 1 },
                (_, i) => new Date().getFullYear() - i,
              ).map((y) => (
                <option key={y} value={`year_${y}`}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Custom-range pickers */}
      {range === 'custom' && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 mb-5 flex flex-wrap items-center gap-3" data-testid="an-custom-range">
          <Filter className="w-4 h-4 text-slate-500" />
          <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="an-custom-start" className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg" />
          <span className="text-slate-400 text-xs">to</span>
          <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="an-custom-end" className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg" />
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        <ClickableKpi testid="an-kpi-orders"     icon={ShoppingCart} label="Orders"        value={loading ? '…' : fmtNum(kpiOrders.total || 0)}              accent="cyan"    growth={growth.orders_pct} sub={loading ? '' : `completed ${fmtNum(kpiOrders.by_status?.completed?.count || 0)}`} tooltip={`All orders created in the selected date range (order.created_at), current + legacy. Sub-line = orders in 'completed' status. Click → Orders list for this period.${cmpLabel}`} onClick={() => goOrders()} />
        <ClickableKpi testid="an-kpi-revenue"    icon={DollarSign}   label="Revenue"       value={loading ? '…' : fmtMoney0(kpiOrders.revenue || 0)}         accent="emerald" growth={growth.revenue_pct} sub="Completed orders only" tooltip={`Sum of total_price for COMPLETED orders in the range. Excludes cancelled/refunded. This is order revenue — NOT wallet recharges. Click → completed orders for this period.${cmpLabel}`} onClick={() => goOrders({ status: 'completed' })} />
        <ClickableKpi testid="an-kpi-avg-check"  icon={Activity}     label="Avg check"     value={loading ? '…' : fmtMoney0(k.avg_check || kpiOrders.aov || 0)} accent="emerald" growth={growth.aov_pct} sub="Revenue / completed orders" tooltip={`Average order value = completed revenue divided by the number of completed orders. Click → completed orders for this period.${cmpLabel}`} onClick={() => goOrders({ status: 'completed' })} />
        <ClickableKpi testid="an-kpi-recharges"  icon={TrendingUp}   label="Recharges"     value={loading ? '…' : fmtNum(kpiRecharges.count || 0)}           accent="amber"   growth={growth.recharges_pct} sub={loading ? '' : fmtMoney0(kpiRecharges.total || 0)} tooltip={`COUNT of customer wallet top-ups in the range (Stripe + PayPal + bank + manual). Click → Transactions · Top-ups for this period.${cmpLabel}`} onClick={() => goTx({ category: 'top_ups' })} />
        <ClickableKpi testid="an-kpi-rch-amount" icon={DollarSign}   label="Recharge total" value={loading ? '…' : fmtMoney0(kpiRecharges.total || 0)}        accent="amber" sub={loading ? '' : `real ${fmtMoney0(kpiRecharges.real_total || 0)} · bonus ${fmtMoney0(kpiRecharges.bonus_total || 0)}`} tooltip="Sum of wallet top-ups INCLUDING bonus credits. Sub-line splits real cash (PayPal/Stripe/Bank/Manual) vs promo/welcome bonus credits. Click → Transactions · Top-ups." onClick={() => goTx({ category: 'top_ups' })} />
        <ClickableKpi testid="an-kpi-registrations" icon={Users} label="Registrations" value={loading ? '…' : fmtNum(k.registrations ?? kpiUsers.new ?? 0)} accent="violet" sub="Sign-ups in period" tooltip="Customer accounts created in the selected range (legacy users by their original registration date). Click → Clients · All new." onClick={() => openClients('new', false)} />
        <ClickableKpi testid="an-kpi-new-clients" icon={Users} label="New clients" value={loading ? '…' : fmtNum(k.new_clients?.with_orders ?? 0)} accent="violet" sub={loading ? '' : `of ${fmtNum(k.new_clients?.count ?? 0)} · ${fmtMoney0(k.new_clients?.revenue ?? 0)} (${k.new_clients?.revenue_share_pct ?? 0}%)`} tooltip={`New clients (registered in period, window = ${k.new_clients?.days ?? 60} days setting) who placed at least one completed order. Click → Clients · New.`} onClick={() => openClients('new', true)} />
        <ClickableKpi testid="an-kpi-closed-cancelled" icon={Activity} label="Closed / Cancelled" value={loading ? '…' : `${fmtNum(kpiOrders.by_status?.closed?.count || 0)} / ${fmtNum(kpiOrders.by_status?.cancelled?.count || 0)}`} accent="rose" sub={loading ? '' : `${fmtMoney0(kpiOrders.by_status?.cancelled?.revenue || 0)} cancelled`} tooltip="Orders closed by an admin (manual close / refund / warranty expiry) and orders cancelled by customer or admin. Click → cancelled orders for this period." onClick={() => goOrders({ status: 'cancelled' })} />
        {/* ECU-finder adoption — single KPI tile with an inline sparkline
            visualising daily adoption %. Click → filtered orders list. */}
        <EcuFinderKpi data={ecuFinderAdoption} loading={loading} onClick={() => goOrders({ ecu_finder_only: true })} />
      </div>

      {/* Time-series chart with metric switcher */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm mb-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="font-bold text-slate-800 text-sm">Time-series</h3>
          <span className="text-[11px] text-slate-400 hidden sm:block" data-testid="an-series-hint">
            Click any bar / point to open the rows behind it
          </span>
          <div className="flex items-center gap-2 text-xs flex-wrap min-w-0 max-w-full">
            <span className="text-slate-500">Metric:</span>
            <div className="inline-flex bg-slate-100 rounded-lg p-1 flex-wrap">
              {[
                { id: 'orders',        label: 'Orders' },
                { id: 'revenue',       label: 'Revenue' },
                { id: 'recharges',     label: 'Recharges' },
                { id: 'registrations', label: 'Registrations' },
              ].map((m) => (
                <button
                  key={m.id}
                  data-testid={`an-metric-${m.id}`}
                  onClick={() => setMetric(m.id)}
                  className={`px-2.5 py-1 rounded text-[11px] font-bold transition-all ${
                    metric === m.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'
                  }`}
                >{m.label}</button>
              ))}
            </div>
            {metric !== 'recharges' && (
              <span className="font-mono tabular-nums text-slate-600 ml-2">
                Σ {metric === 'revenue' ? fmtMoney(series.total || 0) : fmtNum(series.total || 0)}
              </span>
            )}
          </div>
        </div>

        {/* Orders summary strip — count + money side-by-side, the missing
            "how much money did the orders in this period add up to?" answer.
            Mirrors the Recharges strip layout for visual consistency. */}
        {metric === 'orders' && series.totals && (
          <div className="flex flex-wrap items-stretch gap-3 mb-4 pb-3 border-b border-slate-100" data-testid="an-orders-summary">
            <div onClick={() => goOrders()} role="button" title="Open these orders" className="flex-shrink-0 px-4 py-2.5 rounded-lg bg-gradient-to-br from-cyan-600 to-blue-700 text-white shadow-sm cursor-pointer hover:brightness-110 transition" data-testid="an-orders-total-tile">
              <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Total orders</div>
              <div className="text-2xl font-extrabold tabular-nums leading-tight">{fmtNum(series.totals.count || 0)}</div>
              <div className="text-[10px] opacity-80 mt-0.5">{fmtMoney(series.totals.total_amount || 0)} total amount</div>
            </div>
            <div onClick={() => goOrders({ status: 'completed' })} role="button" title="Open the completed orders" className="flex-shrink-0 px-4 py-2.5 rounded-lg bg-gradient-to-br from-emerald-600 to-teal-700 text-white shadow-sm cursor-pointer hover:brightness-110 transition" data-testid="an-orders-completed-tile">
              <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Completed</div>
              <div className="text-2xl font-extrabold tabular-nums leading-tight">{fmtNum(series.totals.completed_count || 0)}</div>
              <div className="text-[10px] opacity-80 mt-0.5">{fmtMoney(series.totals.completed_amount || 0)} revenue</div>
            </div>
            <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
              <SummaryPill testid="an-orders-pill-aov"
                label="Avg order value" value={fmtMoney(series.aov || 0)}
                bg="bg-violet-50" text="text-violet-700" border="border-violet-200" dot="#7c3aed" />
              <SummaryPill testid="an-orders-pill-cancelled"
                label="Cancelled amount" value={fmtMoney(series.totals.cancelled_amount || 0)}
                sub={`${fmtNum(series.totals.cancelled_count || 0)} orders`}
                bg="bg-rose-50" text="text-rose-700" border="border-rose-200" dot="#ef4444" />
              <SummaryPill testid="an-orders-pill-completion"
                label="Completion rate"
                value={series.totals.count
                  ? `${((series.totals.completed_count / series.totals.count) * 100).toFixed(1)}%`
                  : '—'}
                sub={`${fmtNum(series.totals.completed_count || 0)} / ${fmtNum(series.totals.count || 0)}`}
                bg="bg-emerald-50" text="text-emerald-700" border="border-emerald-200" dot="#10b981" />
              <SummaryPill testid="an-orders-pill-cancel-rate"
                label="Cancellation rate"
                value={series.totals.count
                  ? `${((series.totals.cancelled_count / series.totals.count) * 100).toFixed(1)}%`
                  : '—'}
                bg="bg-amber-50" text="text-amber-700" border="border-amber-200" dot="#f59e0b" />
            </div>
          </div>
        )}

        {/* Revenue summary strip — completed-only money context + AOV. */}
        {metric === 'revenue' && (
          <div className="flex flex-wrap items-stretch gap-3 mb-4 pb-3 border-b border-slate-100" data-testid="an-revenue-summary">
            <div onClick={() => goOrders({ status: 'completed' })} role="button" title="Open the completed orders" className="flex-shrink-0 px-4 py-2.5 rounded-lg bg-gradient-to-br from-emerald-600 to-teal-700 text-white shadow-sm cursor-pointer hover:brightness-110 transition" data-testid="an-revenue-total-tile">
              <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Completed revenue</div>
              <div className="text-2xl font-extrabold tabular-nums leading-tight">{fmtMoney(series.total || 0)}</div>
              <div className="text-[10px] opacity-80 mt-0.5">{fmtNum(series.completed_count || 0)} completed orders</div>
            </div>
            <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
              <SummaryPill testid="an-revenue-pill-aov"
                label="Avg order value" value={fmtMoney(series.aov || 0)}
                bg="bg-violet-50" text="text-violet-700" border="border-violet-200" dot="#7c3aed" />
              <SummaryPill testid="an-revenue-pill-cancelled"
                label="Cancelled amount" value={fmtMoney(kpiOrders.by_status?.cancelled?.revenue || 0)}
                sub={`${fmtNum(kpiOrders.by_status?.cancelled?.count || 0)} orders`}
                bg="bg-rose-50" text="text-rose-700" border="border-rose-200" dot="#ef4444" />
              <SummaryPill testid="an-revenue-pill-vs-rch"
                label="vs Recharges (real €)"
                value={fmtMoney((series.total || 0) - (kpiRecharges.real_total || 0))}
                sub={`Revenue ${fmtMoney(series.total || 0)} · RCH ${fmtMoney(kpiRecharges.real_total || 0)}`}
                bg="bg-slate-50" text="text-slate-700" border="border-slate-200" dot="#64748b" />
            </div>
          </div>
        )}

        {/* Registrations summary strip — small but consistent with siblings. */}
        {metric === 'registrations' && (
          <div className="flex flex-wrap items-stretch gap-3 mb-4 pb-3 border-b border-slate-100" data-testid="an-reg-summary">
            <div className="flex-shrink-0 px-4 py-2.5 rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-700 text-white shadow-sm" data-testid="an-reg-total-tile">
              <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">New users</div>
              <div className="text-2xl font-extrabold tabular-nums leading-tight">{fmtNum(series.total || 0)}</div>
              <div className="text-[10px] opacity-80 mt-0.5">Sign-ups in selected period</div>
            </div>
          </div>
        )}

        {/* Recharges summary strip — TWO big totals (Real / All) on the left, per-method pills on the right */}
        {metric === 'recharges' && series.method_totals && (
          <div className="flex flex-wrap items-stretch gap-3 mb-4 pb-3 border-b border-slate-100" data-testid="an-recharges-summary">
            <div onClick={() => goTx({ category: 'top_ups' })} role="button" title="Open these top-ups" className="flex-shrink-0 px-4 py-2.5 rounded-lg bg-gradient-to-br from-cyan-600 to-blue-700 text-white shadow-sm cursor-pointer hover:brightness-110 transition" data-testid="an-total-real-tile">
              <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Total without bonus</div>
              <div className="text-2xl font-extrabold tabular-nums leading-tight">{fmtMoney(series.real_total || 0)}</div>
              <div className="text-[10px] opacity-80 mt-0.5">Real money only</div>
            </div>
            <div onClick={() => goTx({ category: 'top_ups' })} role="button" title="Open these top-ups" className="flex-shrink-0 px-4 py-2.5 rounded-lg bg-gradient-to-br from-slate-800 to-slate-900 text-white shadow-sm cursor-pointer hover:brightness-110 transition" data-testid="an-total-all-tile">
              <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">Total recharged</div>
              <div className="text-2xl font-extrabold tabular-nums leading-tight">{fmtMoney(series.total || 0)}</div>
              <div className="text-[10px] opacity-70 mt-0.5">
                Real {fmtMoney(series.real_total || 0)} · Bonus {fmtMoney(series.bonus_total || 0)}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
              {[
                { k: 'paypal', label: 'PayPal', color: '#3b82f6', bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200'    },
                { k: 'stripe', label: 'Stripe', color: '#8b5cf6', bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200'  },
                { k: 'bank',   label: 'Bank',   color: '#10b981', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
                { k: 'manual', label: 'Manual', color: '#64748b', bg: 'bg-slate-50',   text: 'text-slate-700',   border: 'border-slate-200'   },
                { k: 'bonus',  label: 'Bonus',  color: '#f59e0b', bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200'   },
              ].map(({ k, label, color, bg, text, border }) => {
                const v = k === 'bonus' ? (series.bonus_total || 0) : (series.method_totals?.[k] || 0);
                const pct = series.total ? (v / series.total * 100) : 0;
                return (
                  <div
                    key={k}
                    className={`px-3 py-2 rounded-lg ${bg} ${border} border flex flex-col justify-center min-w-[110px] flex-1`}
                    data-testid={`an-method-pill-${k}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
                      <span className={`text-[10px] uppercase font-bold ${text} opacity-80`}>{label}</span>
                    </div>
                    <div className={`font-bold tabular-nums ${text}`}>{fmtMoney(v)}</div>
                    <div className="text-[10px] text-slate-400 tabular-nums">{pct.toFixed(0)}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <ResponsiveContainer width="100%" height={260}>
          {metric === 'recharges' ? (
            <ComposedChart data={series.buckets} accessibilityLayer={false} onClick={onSeriesClick} style={{ cursor: 'pointer' }} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
              <Tooltip content={<RechargeTooltip />} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="paypal" stackId="r" name="PayPal" fill="#3b82f6" cursor="pointer" onClick={onBucketClick} />
              <Bar dataKey="stripe" stackId="r" name="Stripe" fill="#8b5cf6" cursor="pointer" onClick={onBucketClick} />
              <Bar dataKey="bank"   stackId="r" name="Bank"   fill="#10b981" cursor="pointer" onClick={onBucketClick} />
              <Bar dataKey="manual" stackId="r" name="Manual" fill="#64748b" cursor="pointer" onClick={onBucketClick} />
              <Bar dataKey="bonus"  stackId="r" name="Bonus"  fill="#f59e0b" radius={[4, 4, 0, 0]} cursor="pointer" onClick={onBucketClick} />
              {/* Trend line on top of the stack — gives the eye an instant
                  "total over time" read without reading bar heights. */}
              <Line
                type="monotone" dataKey="value" name="Total"
                stroke="#0f172a" strokeWidth={2.5}
                dot={{ r: 3, fill: '#0f172a' }} activeDot={{ r: 5 }}
              />
            </ComposedChart>
          ) : metric === 'orders' ? (
            /* Dual-axis: count bars (left) + completed-amount line (right).
               Keeps the historical "order count" visual while showing the
               money curve right alongside it. */
            <ComposedChart data={series.buckets} accessibilityLayer={false} onClick={onSeriesClick} style={{ cursor: 'pointer' }} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
              <YAxis yAxisId="count" stroke="#06b6d4" fontSize={11} allowDecimals={false} />
              <YAxis yAxisId="money" orientation="right" stroke="#10b981" fontSize={11}
                     tickFormatter={(v) => v >= 1000 ? `€${(v/1000).toFixed(0)}k` : `€${v}`} />
              <Tooltip content={<OrdersTooltip />} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar  yAxisId="count" dataKey="count"            name="Orders"           fill="#06b6d4" radius={[3, 3, 0, 0]} cursor="pointer" onClick={onBucketClick} />
              <Line yAxisId="money" type="monotone" dataKey="completed_amount" name="Completed €" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line yAxisId="money" type="monotone" dataKey="total_amount"     name="Total €"     stroke="#0f172a" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
            </ComposedChart>
          ) : metric === 'revenue' ? (
            <ComposedChart data={series.buckets} accessibilityLayer={false} onClick={onSeriesClick} style={{ cursor: 'pointer' }} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
              <YAxis yAxisId="money" stroke="#10b981" fontSize={11}
                     tickFormatter={(v) => v >= 1000 ? `€${(v/1000).toFixed(0)}k` : `€${v}`} />
              <YAxis yAxisId="count" orientation="right" stroke="#06b6d4" fontSize={11} allowDecimals={false} />
              <Tooltip content={<RevenueTooltip />} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar  yAxisId="money" dataKey="amount" name="Revenue"          fill="#10b981" radius={[3, 3, 0, 0]} cursor="pointer" onClick={onBucketClick} />
              <Line yAxisId="count" type="monotone" dataKey="count" name="Completed orders" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </ComposedChart>
          ) : (
            <LineChart data={series.buckets} accessibilityLayer={false} onClick={onSeriesClick} style={{ cursor: 'pointer' }} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
              <Tooltip formatter={(v) => fmtNum(v)} />
              <Line type="monotone" dataKey="value" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5, cursor: 'pointer', onClick: onBucketClick }} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Breakdown row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        {/* Status pie — tooltip shows count + revenue + percentage. */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-800 text-sm">Orders by status</h3>
            {activeFilterCount > 0 && <FilterAppliedBadge n={activeFilterCount} total={breakdown?.total} />}
          </div>
          {breakdown?.by_status?.length === 1 ? (
            /* A pie of one slice is noise — show the single status as a row. */
            <div className="py-12 text-center" data-testid="an-status-single">
              <div className="text-2xl font-extrabold text-slate-800 capitalize">
                {breakdown.by_status[0].key} {fmtNum(breakdown.by_status[0].count)}
              </div>
              <div className="text-xs text-slate-500 mt-1">100% of orders in this period</div>
            </div>
          ) : breakdown?.by_status?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={breakdown.by_status} dataKey="count" nameKey="key" outerRadius={75} label={(e) => e.key}
                     onClick={(e) => goOrders({ status: e?.key || e?.payload?.key })} style={{ cursor: 'pointer' }}>
                  {breakdown.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<StatusTooltip total={breakdown.total} />} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-10">No data</p>}
        </div>

        {/* Manufacturer bar — Orders|Revenue toggle. Recharts sorts by the
            field we pass in via `dataKey`; we also pre-sort the array. */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="font-bold text-slate-800 text-sm">Top manufacturers</h3>
            <div className="flex items-center gap-2">
              {activeFilterCount > 0 && <FilterAppliedBadge n={activeFilterCount} total={breakdown?.total} />}
              <BreakdownToggle mode={breakdownMode} setMode={setBreakdownMode} testid="an-breakdown-mfr" />
            </div>
          </div>
          {breakdown?.by_manufacturer?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={[...breakdown.by_manufacturer]
                  .sort((a, b) => (b[breakdownMode === 'revenue' ? 'revenue' : 'count'] || 0) - (a[breakdownMode === 'revenue' ? 'revenue' : 'count'] || 0))
                  .slice(0, 10)}
                layout="vertical" margin={{ left: 30 }}
                accessibilityLayer={false}
                onClick={(e) => e?.activeLabel && goOrders({ manufacturer: e.activeLabel })}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#64748b" fontSize={11}
                       tickFormatter={(v) => breakdownMode === 'revenue' ? (v >= 1000 ? `€${(v/1000).toFixed(0)}k` : `€${v}`) : fmtNum(v)} />
                <YAxis type="category" dataKey="key" stroke="#64748b" fontSize={11} width={80} />
                <Tooltip content={<BreakdownTooltip />} />
                <Bar dataKey={breakdownMode === 'revenue' ? 'revenue' : 'count'} fill="#7c3aed" radius={[0, 4, 4, 0]}
                     cursor="pointer" onClick={(e) => { const k = e?.payload?.key || e?.key; if (k) goOrders({ manufacturer: k }); }} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-10">No data</p>}
        </div>

        {/* ECU type bar — same toggle pattern. */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="font-bold text-slate-800 text-sm">Top ECU types</h3>
            <div className="flex items-center gap-2">
              {activeFilterCount > 0 && <FilterAppliedBadge n={activeFilterCount} total={breakdown?.total} />}
              <BreakdownToggle mode={breakdownMode} setMode={setBreakdownMode} testid="an-breakdown-ecu" />
            </div>
          </div>
          {breakdown?.by_ecu_type?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={[...breakdown.by_ecu_type]
                  .sort((a, b) => (b[breakdownMode === 'revenue' ? 'revenue' : 'count'] || 0) - (a[breakdownMode === 'revenue' ? 'revenue' : 'count'] || 0))
                  .slice(0, 10)}
                layout="vertical" margin={{ left: 50 }}
                accessibilityLayer={false}
                onClick={(e) => e?.activeLabel && goOrders({ ecu_type: e.activeLabel })}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#64748b" fontSize={11}
                       tickFormatter={(v) => breakdownMode === 'revenue' ? (v >= 1000 ? `€${(v/1000).toFixed(0)}k` : `€${v}`) : fmtNum(v)} />
                <YAxis type="category" dataKey="key" stroke="#64748b" fontSize={11} width={100} />
                <Tooltip content={<BreakdownTooltip />} />
                <Bar dataKey={breakdownMode === 'revenue' ? 'revenue' : 'count'} fill="#10b981" radius={[0, 4, 4, 0]}
                     cursor="pointer" onClick={(e) => { const k = e?.payload?.key || e?.key; if (k) goOrders({ ecu_type: k }); }} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-10">No data</p>}
        </div>
      </div>

      {/* Revenue vs Recharges insight — single-glance read of cashflow direction. */}
      <RevenueVsRechargesCard kpiOrders={kpiOrders} kpiRecharges={kpiRecharges} loading={loading} />

      {/* Recharge methods + Top customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="mb-3">
            <h3 className="font-bold text-slate-800 text-sm">Recharges by method</h3>            <p className="text-[11px] text-slate-500 mt-0.5">
              Split of <b>real-money</b> top-ups (bonuses excluded) by payment method in the selected period.
              {kpiRecharges.count ? ` · ${fmtNum(kpiRecharges.count)} txns · ${fmtMoney(kpiRecharges.real_total || 0)} total` : ''}
            </p>
          </div>
          {kpiRecharges?.by_method?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={kpiRecharges.by_method} dataKey="amount" nameKey="method" outerRadius={85}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => goTx({ category: 'top_ups', description: e?.method || e?.payload?.method })}
                  label={(e) => {
                    const pct = kpiRecharges.real_total ? (e.amount / kpiRecharges.real_total * 100) : 0;
                    return `${e.method}: ${fmtMoney(e.amount)} (${pct.toFixed(0)}%)`;
                  }}
                >
                  {kpiRecharges.by_method.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend />
                <Tooltip formatter={(v, _, p) => [`${fmtMoney(v)} · ${p.payload.count}x`, p.payload.method]} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-10">No recharges in this window</p>}
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-800 text-sm">Top customers</h3>
            <div className="inline-flex bg-slate-100 rounded p-0.5 text-[10px] font-bold">
              {[
                { id: 'revenue',  label: 'Revenue' },
                { id: 'orders',   label: 'Orders' },
                { id: 'recharges', label: 'Recharges' },
              ].map((m) => (
                <button key={m.id}
                  data-testid={`an-top-${m.id}`}
                  onClick={() => setTopMetric(m.id)}
                  className={`px-2 py-0.5 rounded ${topMetric === m.id ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
                >{m.label}</button>
              ))}
            </div>
          </div>
          <div className="max-h-[260px] overflow-y-auto pr-1">
            {topCust?.items?.length ? topCust.items.map((c, idx) => (
              <button
                type="button"
                key={c.user_id || idx}
                data-testid={`an-top-customer-${idx}`}
                title={topMetric === 'recharges' ? 'Open this customer\u2019s top-ups' : 'Open this customer\u2019s orders'}
                onClick={() => (topMetric === 'recharges'
                  ? goTx({ category: 'top_ups', user_email: c.user_email || '' })
                  : goOrders({ customer_email: c.user_email || '' }))}
                className="w-full text-left flex items-center justify-between py-1.5 px-2 rounded hover:bg-cyan-50 text-sm transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">
                    {[c.first_name, c.surname].filter(Boolean).join(' ') || c.user_email || '—'}
                    {c.account_id && <span className="ml-1 text-[10px] font-mono text-slate-400">#{c.account_id}</span>}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">{c.user_email}</p>
                </div>
                <span className="font-mono font-bold text-slate-800 tabular-nums">
                  {topMetric === 'revenue' && fmtMoney(c.revenue)}
                  {topMetric === 'orders' && fmtNum(c.orders)}
                  {topMetric === 'recharges' && fmtMoney(c.amount)}
                </span>
              </button>
            )) : <p className="text-xs text-slate-400 text-center py-6">No data</p>}
          </div>
        </div>
      </div>

      {/* Clients — New / Top / Returning / Dormant (spec: Oct 2026).
          One block with tabs instead of four cards; a row opens the
          client card drawer without leaving Analytics. */}
      <AnalyticsClientsBlock
        token={token}
        range={range}
        customStart={customStart}
        customEnd={customEnd}
        filters={filters}
        activeTab={clientsTab}
        onlyWithOrdersDefault={clientsOnlyWithOrders}
        onTabChange={setClientsTab}
      />

      {/* Geography — country mix of orders / revenue / customers for the
          selected period. Lives right under the clients block because
          both answer "who is buying", just on different axes. */}
      <GeographyBlock
        token={token}
        range={range}
        customStart={customStart}
        customEnd={customEnd}
      />

      {/* Top vehicles (full manufacturer + model combo) + Problem queue.
          The bar chart up top already groups by manufacturer alone — this
          row drills down one level (manufacturer + model) so the admin
          can see what exact car/ECU is driving the orders. Problem queue
          duplicates the dashboard widget here so admins who land on the
          analytics page first don't miss it. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm" data-testid="an-top-vehicles">
          <div className="mb-3">
            <h3 className="font-bold text-slate-800 text-sm">Top vehicles</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Most-ordered manufacturer / model combinations for the selected period (top 10).
            </p>
          </div>
          {!topVehicles || (topVehicles.items || []).length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-10">No vehicle data in this window.</p>
          ) : (
            <div className="max-h-[260px] overflow-y-auto pr-1">
              {topVehicles.items.map((v, idx) => {
                const totalTop = topVehicles.items.reduce((acc, x) => acc + (x.count || 0), 0) || 1;
                const pct = ((v.count || 0) / totalTop) * 100;
                return (
                  <button
                    type="button"
                    key={`${v.label}-${idx}`}
                    onClick={() => goOrders({ manufacturer: v.manufacturer || '', model: v.model || '' })}
                    title="Open the orders for this vehicle"
                    className="w-full text-left py-1.5 px-2 rounded hover:bg-violet-50 transition-colors"
                    data-testid={`an-top-vehicle-${idx}`}
                  >
                    <div className="flex items-center justify-between text-sm gap-2">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-[10px] font-bold text-slate-400 w-4 text-right">{idx + 1}.</span>
                        <span className="truncate font-medium text-slate-800">{v.label || '—'}</span>
                      </span>
                      <span className="font-mono font-bold text-slate-800 tabular-nums whitespace-nowrap text-xs">
                        {v.count} · <span className="text-emerald-700">{fmtMoney(v.revenue)}</span>
                      </span>
                    </div>
                    {/* Inline % bar — gives a sense of distribution at a glance */}
                    <div className="mt-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-violet-500 rounded-full" style={{ width: `${pct.toFixed(1)}%` }} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm" data-testid="an-problem-queue">
          <div className="mb-3">
            <h3 className="font-bold text-slate-800 text-sm">Problem queue</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Live operational counters. Independent of the date range — always shows the current backlog
              the admin should act on.
            </p>
          </div>
          {!problemQueue ? (
            <p className="text-xs text-slate-400 text-center py-10">Loading…</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <ProblemTile
                label="Open tickets"
                hint={`>${4}h without an admin reply`}
                value={problemQueue.open_tickets ?? 0}
                testid="an-pq-open-tickets"
                onClick={() => typeof go === 'function' && go('tickets')}
              />
              <ProblemTile
                label="Pending orders"
                hint="new / pending / received / working"
                value={problemQueue.pending_orders ?? 0}
                testid="an-pq-pending-orders"
                onClick={() => typeof go === 'function' && go('orders', { orders: { status: 'pending' } })}
              />
              <ProblemTile
                label="Failed payments"
                hint="last 30 days"
                value={problemQueue.failed_payments ?? 0}
                testid="an-pq-failed-payments"
                onClick={() => typeof go === 'function' && go('transactions', { transactions: { status: 'failed' } })}
              />
              <ProblemTile
                label="Stuck EVC"
                hint="pending / error > 1h"
                value={problemQueue.stuck_evc ?? 0}
                testid="an-pq-stuck-evc"
                onClick={() => typeof go === 'function' && go('evc')}
              />
            </div>
          )}
        </div>
      </div>

      {/* Filters + Export */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" /> Filters & Export
            </h3>
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 bg-cyan-100 text-cyan-800 text-[10px] font-bold rounded-full" data-testid="an-active-filter-count">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setFilters({ manufacturer: '', ecu_type: '', status: '', user_email: '', account_id: '' })}
                    data-testid="an-filters-reset"
                    disabled={activeFilterCount === 0}
                    className="px-3 py-1.5 text-xs font-bold bg-slate-100 hover:bg-slate-200 rounded flex items-center gap-1.5 disabled:opacity-50">
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
            <button onClick={() => downloadCsv('orders')}
                    data-testid="an-export-orders"
                    className="px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded flex items-center gap-1.5">
              <Download className="w-3 h-3" /> Orders CSV
            </button>
            <button onClick={() => downloadCsv('recharges')}
                    data-testid="an-export-recharges"
                    className="px-3 py-1.5 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded flex items-center gap-1.5">
              <Download className="w-3 h-3" /> Recharges CSV
            </button>
            <button onClick={() => downloadCsv('registrations')}
                    data-testid="an-export-users"
                    className="px-3 py-1.5 text-xs font-bold text-white bg-violet-600 hover:bg-violet-700 rounded flex items-center gap-1.5">
              <Download className="w-3 h-3" /> Users CSV
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input data-testid="an-f-manufacturer" value={filters.manufacturer} onChange={(e) => setFilters({ ...filters, manufacturer: e.target.value })} placeholder="Manufacturer" className="px-3 py-2 text-sm border border-slate-300 rounded-lg" />
          <input data-testid="an-f-ecu" value={filters.ecu_type} onChange={(e) => setFilters({ ...filters, ecu_type: e.target.value })} placeholder="ECU type" className="px-3 py-2 text-sm border border-slate-300 rounded-lg" />
          <input data-testid="an-f-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} placeholder="Status (e.g. completed)" className="px-3 py-2 text-sm border border-slate-300 rounded-lg" />
          <input data-testid="an-f-email" value={filters.user_email} onChange={(e) => setFilters({ ...filters, user_email: e.target.value })} placeholder="Customer email" className="px-3 py-2 text-sm border border-slate-300 rounded-lg" />
          <input data-testid="an-f-account" value={filters.account_id} onChange={(e) => setFilters({ ...filters, account_id: e.target.value })} placeholder="Account #" className="px-3 py-2 text-sm border border-slate-300 rounded-lg" />
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          ⚡ Filters apply <b>live</b> as you type — they re-filter the three breakdown charts above (<i>Orders by status</i>, <i>Top manufacturers</i>, <i>Top ECU types</i>).
          The date-range pills at the top govern <b>everything</b> on this page (KPIs, time-series, breakdowns, <i>and</i> CSV exports).
        </p>
      </div>

      {/* EVC sales analytics — separate concern, has its own range picker */}
      <EVCAnalytics token={token} compact />
    </div>
  );
}

/**
 * FilterAppliedBadge — tiny pill rendered inside a breakdown-card header to
 * make it obvious that the filter row at the bottom is shaping that chart.
 * Without this signal the filters look like they "do nothing" because the
 * change is silent — a chart with 30 brands quietly drops to 1.
 */
function FilterAppliedBadge({ n, total }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-50 border border-cyan-200 text-cyan-800 text-[10px] font-bold rounded-full"
      title={`${n} filter${n === 1 ? '' : 's'} applied · ${total ?? '?'} matching orders`}
      data-testid="an-breakdown-filtered-badge"
    >
      <Filter className="w-2.5 h-2.5" /> Filtered{total != null ? ` · ${total}` : ''}
    </span>
  );
}

/**
 * Compact stat pill used inside the Orders/Revenue summary strips.
 * Keeps strip layout consistent with the existing Recharges per-method tiles.
 */
function SummaryPill({ label, value, sub, bg, text, border, dot, testid }) {
  return (
    <div
      className={`px-3 py-2 rounded-lg ${bg} ${border} border flex flex-col justify-center min-w-[104px] sm:min-w-[130px] max-w-full flex-1`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full" style={{ background: dot }} />
        <span className={`text-[10px] uppercase font-bold ${text} opacity-80`}>{label}</span>
      </div>
      <div className={`font-bold tabular-nums ${text}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400 tabular-nums truncate">{sub}</div>}
    </div>
  );
}

/** Inline toggle pair for breakdown charts: Orders ↔ Revenue. */
function BreakdownToggle({ mode, setMode, testid }) {
  return (
    <div className="inline-flex bg-slate-100 rounded p-0.5 text-[10px] font-bold" data-testid={testid}>
      {[
        { id: 'orders',  label: 'Orders' },
        { id: 'revenue', label: 'Revenue' },
      ].map((m) => (
        <button
          key={m.id}
          data-testid={`${testid}-${m.id}`}
          onClick={() => setMode(m.id)}
          className={`px-2 py-0.5 rounded ${mode === m.id ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
        >{m.label}</button>
      ))}
    </div>
  );
}

/** Tooltip for the manufacturer/ECU bars — always shows BOTH count and revenue. */
function BreakdownTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload || {};
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-[11px]" data-testid="an-breakdown-tooltip">
      <div className="font-bold text-slate-800 mb-1">{p.key}</div>
      <Row k="Orders"  v={fmtNum(p.count || 0)}      color="#06b6d4" />
      <Row k="Revenue" v={fmtMoney(p.revenue || 0)}  color="#10b981" bold />
    </div>
  );
}

/** Tooltip for the Orders-by-status pie — count + revenue + share. */
function StatusTooltip({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload || {};
  const pct = total ? ((p.count || 0) / total) * 100 : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-[11px]" data-testid="an-status-tooltip">
      <div className="font-bold text-slate-800 mb-1 capitalize">{p.key}</div>
      <Row k="Orders"  v={fmtNum(p.count || 0)}     color="#06b6d4" />
      <Row k="Revenue" v={fmtMoney(p.revenue || 0)} color="#10b981" bold />
      <Row k="Share"   v={`${pct.toFixed(1)}%`}     color="#64748b" />
    </div>
  );
}

/**
 * Revenue vs Recharges — single-row insight card.
 * Helps answer: "Are users consuming more than they recharge in this period?"
 * Net negative = users burning wallet faster than they top up.
 */
function RevenueVsRechargesCard({ kpiOrders, kpiRecharges, loading }) {
  const rev = Number(kpiOrders.revenue || 0);
  const rch = Number(kpiRecharges.real_total || 0);
  const diff = rev - rch;
  const positive = diff >= 0;
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm mb-5" data-testid="an-rev-vs-rch">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-slate-500" /> Revenue vs Recharges
        </h3>
        <p className="text-[11px] text-slate-500">Order revenue minus real-money top-ups for the selected period (bonus excluded).</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="an-rvr-revenue">
          <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Completed order revenue</div>
          <div className="text-xl font-extrabold tabular-nums text-emerald-800 mt-0.5">{loading ? '…' : fmtMoney(rev)}</div>
        </div>
        <div className="px-4 py-3 rounded-lg bg-cyan-50 border border-cyan-200" data-testid="an-rvr-recharges">
          <div className="text-[10px] uppercase font-bold tracking-wider text-cyan-700">Real money recharged</div>
          <div className="text-xl font-extrabold tabular-nums text-cyan-800 mt-0.5">{loading ? '…' : fmtMoney(rch)}</div>
        </div>
        <div
          className={`px-4 py-3 rounded-lg border ${positive ? 'bg-slate-50 border-slate-200' : 'bg-amber-50 border-amber-200'}`}
          data-testid="an-rvr-diff"
          title={positive
            ? 'Users consumed more than they topped up in this period (positive net for the business).'
            : 'Users topped up more than they consumed in this period (wallet balances growing).'}
        >
          <div className={`text-[10px] uppercase font-bold tracking-wider ${positive ? 'text-slate-700' : 'text-amber-700'}`}>
            Difference (rev − recharges)
          </div>
          <div className={`text-xl font-extrabold tabular-nums mt-0.5 ${positive ? 'text-slate-800' : 'text-amber-800'}`}>
            {loading ? '…' : `${positive ? '+' : ''}${fmtMoney(diff)}`}
          </div>
          <div className={`text-[10px] mt-0.5 ${positive ? 'text-slate-500' : 'text-amber-700'}`}>
            {positive ? 'Users consumed more than they recharged.' : 'Users recharged more than they consumed.'}
          </div>
        </div>
      </div>
    </div>
  );
}
